// src/utils/seasonRanking.ts
//
// Čisté funkce nad publikovanými výsledky (races/{id}/results_entries +
// results_stages): výchozí řazení tabulky závodu, jedna definice procent
// a roční hodnocení „nejlepších N závodů".
//
// Algoritmus ročního hodnocení je PŘESUN 1:1 z UserResults.generateYearlySummary
// (krok 7 redesignu, „nesmí se změnit") – včetně pořadí střelců při shodě
// průměru a výběru škrtnutých závodů při shodě hodnot. Shodu s původním kódem
// hlídá seasonRanking.test.ts (doslovné kopie původních bloků jako reference).
//
// Procenta (backlog #28): tabulka závodu brala % k nejlepšímu z PRÁVĚ
// VYFILTROVANÝCH řádků (při filtru kategorie k vítězi kategorie), roční
// hodnocení k vítězi celého závodu. Platí jedna definice – k vítězi celého
// závodu (computeRacePercent + raceTopScore nad nefiltrovanými výsledky):
// roční hodnocení se měnit nesmí, PractiScore „Match %" počítá totéž a tabule
// (DisplayView) procenta vůbec neukazuje. Uložené pole matchPercent se pro
// zobrazení NEPOUŽÍVÁ – live publikace do kroku 9 do něj psala podíl
// z maximálně dosažitelného skóre (import a publikace od kroku 9 podíl
// z vítěze = computeRacePercent), a starší záznamy v DB zůstávají.
//
// Kategorie záznamu se čte přes categories.resolveEntryCategoryId: platné
// categoryId (publikace od kroku 9, nováčci) má přednost před legacy textem.
//
// Jméno řádku (bezpečnost a): resultEntryName – e-mail ani uid, které jako
// jméno uložila dřívější verze živého bodování, se neukážou („St. č. N",
// jinak „#číslo"); jiné jméno 1:1 jako dřív.

import type { DisciplineId } from "./disciplines";
import { resolveEntryCategoryId, type ResultCategoryId } from "./categories";
import { orderByShootOff, resolveShootOff, shootOffIdOf, tiebreakerOf } from "./shootOff";
import {
  parseHitCounts,
  publishedTieRulesApply,
  rankByTieRules,
  shootOffPlacesOf,
  type TieRaceLike,
  type TieRankInfo,
} from "./tieRules";
import { resultEntryName } from "./shooterName";

// ─────────────────────────────────────────────────────────────
//  Typy dat
// ─────────────────────────────────────────────────────────────

/** Dokument races/{raceId}/results_entries/{key} – jen pole, která čteme. */
export interface RaceResultEntry {
  /**
   * Klíč střelce = členské číslo ("012"), u střelců bez čísla startovní číslo
   * (publikace od kroku 9 doplněné na 3 místa jako import – viz publishResults).
   */
  sequenceNumber: string;
  rawName: string;
  /** Legacy text kategorie (zůstává kvůli zpětné kompatibilitě). */
  category: string;
  /**
   * Id kategorie z číselníku categories.ts – zapisuje publikace z live
   * scoringu od kroku 9 (null = nezařazeno; starší záznamy a import pole
   * nemají). Číst přes resolveEntryCategoryId(entry), ne napřímo.
   */
  categoryId?: ResultCategoryId | null;
  /** Nástřel (body) – jediný zdroj pro pořadí i procenta. */
  matchPoints: number;
  /** Uložené % – sémantika závisí na zdroji, pro zobrazení nepoužívat (viz hlavička). */
  matchPercent: number;
  startNumber?: number;
  division?: string;
  valid?: boolean;
  /** "live_scoring" | "xlsx_import" | "practiscore_zip" */
  source?: string;
  /**
   * Rozstřel při shodě bodů: vyšší = lepší, chybí = bez rozstřelu (číst přes
   * tiebreakerOf z utils/shootOff.ts). Zapisuje publikace z live scoringu
   * a obrazovka Rozstřely v administraci.
   */
  tiebreaker?: number;
  /** uid střelce (publikace z live scoringu od nice-to-have); import ho nemá. */
  userId?: string;
  /**
   * Zásahy podle hodnot (publikace živého závodu od fáze 4b – pravidla
   * shody LKS, utils/tieRules.ts): [{ value: 30.1, count: 2 }, …] od
   * nejvyšší hodnoty; jen když je rozpad na rány úplný (hitCountsComplete).
   */
  hitCounts?: Array<{ value: number; count: number }>;
  /** Rozpad na rány úplný a souhlasný na všech stanovištích (fáze 4b). */
  hitCountsComplete?: boolean;
}

/** Jedno stanoviště z results_stages/{key}.stages[]. */
export interface RaceStageResult {
  name: string;
  points: number;
}

/** Výsledek jednoho střelce v závodě; stages = null, když results_stages chybí. */
export interface RaceResult {
  entry: RaceResultEntry;
  stages: RaceStageResult[] | null;
}

/** Minimální tvar závodu pro roční hodnocení (Race z useRaces ho splňuje). */
export interface SeasonRace {
  id: string;
  name: string;
  /** Datum závodu "YYYY-MM-DD" (pole races.date). */
  date: string;
  year: number;
  discipline: DisciplineId;
  includeInResults: boolean;
  enableResults: boolean;
  /** Pravidla shody 4b (publishedTieRulesApply): živé bodování, stav, ukončení, počet míst rozstřelu. */
  liveScoring?: boolean;
  status?: string;
  /** Millis ukončení (0 = není). */
  finishedAt?: number;
  shootOffPlaces?: number;
}

// ─────────────────────────────────────────────────────────────
//  Členské číslo
// ─────────────────────────────────────────────────────────────

/**
 * Členské číslo ve tvaru klíče výsledků: doplněné zleva nulami na 3 místa
 * (1:1 s dosavadním `normalized` v UserResults – "7" → "007", "1000" → "1000").
 */
export function normalizeSequenceNumber(value: string | number): string {
  return value.toString().padStart(3, "0");
}

/** Je řádek výsledků přihlášeného střelce? (null/undefined na kterékoli straně = ne.) */
export function isSameShooter(
  a: string | number | null | undefined,
  b: string | number | null | undefined
): boolean {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return normalizeSequenceNumber(a) === normalizeSequenceNumber(b);
}

// ─────────────────────────────────────────────────────────────
//  Tabulka závodu
// ─────────────────────────────────────────────────────────────

/**
 * Výchozí řazení tabulky závodu: body sestupně, při shodě rozstřel
 * (tiebreaker sestupně – utils/shootOff.ts), jinak pořadí dokumentů
 * (Firestore vrací podle id, tj. členského čísla). Bez rozstřelů 1:1
 * s původním `arr.sort((a, b) => b.entry.matchPoints - a.entry.matchPoints)`.
 * Vrací nové pole, vstup nemění.
 */
export function sortRaceEntries(results: RaceResult[]): RaceResult[] {
  // Fáze 4a (C2): rozstřel s otiskem jiné skupiny se nepoužije
  // (orderByShootOff); bez otisku 1:1 s dřívějším
  // `b.matchPoints - a.matchPoints || tiebreaker(b) - tiebreaker(a)` –
  // stabilní řazení podle bodů a pak uvnitř shody podle hodnot dá totéž.
  const byPoints = [...results].sort((a, b) => b.entry.matchPoints - a.entry.matchPoints);
  return orderByShootOff(
    byPoints,
    (r) => r.entry.matchPoints,
    (r) => shootOffIdOf(r.entry),
    (r) => tiebreakerOf(r.entry)
  );
}

/**
 * Výsledky jedné kategorie v pořadí kategorie (filtr kategorie, pořadí
 * v kategorii ve sdílení a v „Moje sezóna"): seřazené znovu jen v rámci
 * kategorie – rozstřel zadaný v kategorii (s otiskem skupiny kategorie)
 * tak rozhoduje i ve zveřejněných výsledcích (fáze 4a, C2). Bez rozstřelů
 * s otiskem totéž co filtr seřazeného celku.
 */
export function sortCategoryEntries(
  results: RaceResult[],
  inCategory: (r: RaceResult) => boolean
): RaceResult[] {
  return sortRaceEntries(results.filter(inCategory));
}

// ── Pořadí a místa zveřejněných výsledků (fáze 4b) ─────────────────────────

/** Seřazené výsledky s místy (sdílené místo = stejné číslo). */
export interface RankedRaceResults {
  ordered: RaceResult[];
  /** Místo každého výsledku (1 = první). */
  places: Map<RaceResult, number>;
  /** Jak se shoda rozhodla; null = starší závod (místo = pořadí). */
  ties: Map<RaceResult, TieRankInfo> | null;
}

const entryTieInput = (r: RaceResult, index: number) => ({
  total: r.entry.matchPoints,
  ranked: true,
  shootOffId: shootOffIdOf(r.entry),
  tiebreaker: tiebreakerOf(r.entry),
  hits: r.entry.hitCountsComplete === true ? parseHitCounts(r.entry.hitCounts) : null,
  // Startovní číslo; záznam bez něj (import) za ně v pořadí dokumentů
  order:
    typeof r.entry.startNumber === "number" && Number.isFinite(r.entry.startNumber)
      ? r.entry.startNumber
      : 1e9 + index,
});

/**
 * Zveřejněné výsledky závodu řadit podle pravidel 4b? Závod živého bodování
 * s pravidly 4b (publishedTieRulesApply) a výsledky zveřejněné verzí 4b –
 * aspoň jeden živý záznam s polem hitCountsComplete (zapisuje ho jen
 * publikace 4b). Import nebo zveřejnění starší verzí = dosavadní pořadí.
 */
export function publishedRulesFor(results: RaceResult[], race: TieRaceLike | null | undefined): boolean {
  return (
    !!race &&
    publishedTieRulesApply(race) &&
    results.some((r) => r.entry.source === "live_scoring" && typeof r.entry.hitCountsComplete === "boolean")
  );
}

function rankWithRules(
  results: RaceResult[],
  race: TieRaceLike,
  whole: RaceResult[] | null
): RankedRaceResults {
  const indexOf = new Map<RaceResult, number>();
  (whole || results).forEach((r, i) => indexOf.set(r, i));
  const input = (r: RaceResult) => entryTieInput(r, indexOf.has(r) ? (indexOf.get(r) as number) : 0);
  // Kategorie: shodu rozhodne i platný celkový rozstřel, jehož je částí
  const fallbackShootOff = whole
    ? (members: RaceResult[]): number[] | null => {
        const points = members[0].entry.matchPoints;
        const overall = whole.filter((r) => r.entry.matchPoints === points);
        if (overall.length < 2) return null;
        const resolution = resolveShootOff(overall.map((r) => shootOffIdOf(r.entry)), overall.map((r) => tiebreakerOf(r.entry)));
        if (resolution.state !== "decided") return null;
        const values = members.map((m) => {
          const i = overall.indexOf(m);
          return i < 0 ? 0 : resolution.effective[i];
        });
        if (values.some((v) => !(v > 0))) return null;
        return new Set(values).size === values.length ? values : null;
      }
    : undefined;
  const ranking = rankByTieRules(results, input, { shootOffPlaces: shootOffPlacesOf(race), fallbackShootOff, exactTotals: true });
  const places = new Map<RaceResult, number>();
  ranking.ordered.forEach((r) => places.set(r, (ranking.info.get(r) as TieRankInfo).place as number));
  return { ordered: ranking.ordered, places, ties: ranking.info };
}

/**
 * Pořadí a místa celého závodu; `results` seřazené jako je vrací
 * loadRaceResults (sortRaceEntries). Starší závod (a import): přesně jako
 * dřív – pořadí vstupu a místo = pořadí. Závod živého bodování ukončený od
 * nasazení 4b: pravidla shody LKS (rozstřel o prvních X míst, počet zásahů
 * z hitCounts, stejné místo podle startovního čísla).
 */
export function rankRaceEntries(results: RaceResult[], race?: TieRaceLike | null): RankedRaceResults {
  if (race && publishedRulesFor(results, race)) return rankWithRules(results, race, null);
  // Starší závod: vstup je už seřazený (loadRaceResults / sortRaceEntries) –
  // pořadí beze změny, místo = pořadí
  const ordered = results.slice();
  const places = new Map<RaceResult, number>();
  ordered.forEach((r, i) => places.set(r, i + 1));
  return { ordered, places, ties: null };
}

/**
 * Pořadí a místa v kategorii (filtr kategorie, pořadí v kategorii ve
 * sdílení a v „Moje sezóna“) – jako sortCategoryEntries, u závodu 4b podle
 * pravidel shody v rámci kategorie (X míst v kategorii).
 */
export function rankCategoryEntries(
  results: RaceResult[],
  inCategory: (r: RaceResult) => boolean,
  race?: TieRaceLike | null
): RankedRaceResults {
  if (race && publishedRulesFor(results, race)) return rankWithRules(results.filter(inCategory), race, results);
  const ordered = sortCategoryEntries(results, inCategory);
  const places = new Map<RaceResult, number>();
  ordered.forEach((r, i) => places.set(r, i + 1));
  return { ordered, places, ties: null };
}

/**
 * Body vítěze závodu = maximum matchPoints přes VŠECHNY výsledky (bez filtru
 * kategorie); bez výsledků 0. 1:1 s `topScore` ročního hodnocení.
 */
export function raceTopScore(results: RaceResult[] | null | undefined): number {
  if (!results || results.length === 0) return 0;
  return results.reduce((max, r) => Math.max(max, r.entry.matchPoints), -Infinity);
}

export interface RacePercentContext {
  /** Body vítěze závodu (raceTopScore nad nefiltrovanými výsledky). */
  topScore: number;
}

/**
 * Jedna definice procent: podíl bodů střelce na bodech vítěze závodu,
 * zaokrouhlený na 2 desetinná místa. topScore ≤ 0 → 0. 1:1 s výpočtem
 * hodnoty závodu v ročním hodnocení.
 *
 * Zaokrouhlení na 2 místa je ZÁMĚRNĚ shodné s hodnotou, která vstupuje do
 * ročního hodnocení (raceValues a z nich průměr). Tabulka závodu i roční
 * tabulka pak zobrazují fmtFixed(value, 1), tj. zaokrouhlují podruhé – proti
 * přímému zaokrouhlení podílu na 1 místo se může lišit nejvýš o 0,1 (např.
 * 12,345 % → 12,35 → „12,4" místo „12,3"). Priorita je stejné číslo v obou
 * tabulkách, ne matematicky přesnější jednotlivá hodnota.
 */
export function computeRacePercent(
  entry: Pick<RaceResultEntry, "matchPoints">,
  context: RacePercentContext
): number {
  return context.topScore > 0
    ? Math.round((entry.matchPoints / context.topScore) * 10000) / 100
    : 0;
}

// ─────────────────────────────────────────────────────────────
//  Roční hodnocení
// ─────────────────────────────────────────────────────────────

/** "percent" = % z vítěze závodu, "points" = nástřel. */
export type SeasonValueMode = "percent" | "points";

/** Počet nejlepších závodů v průměru; "all" = všechny závody roku. */
export type BestRacesCount = number | "all";

export interface SeasonRankingInput {
  /** Všechny závody (libovolné roky) – funkce si vybere rok, disciplínu a hodnocené. */
  races: SeasonRace[];
  /**
   * Výsledky podle id závodu. Chybějící klíč = ještě nenačteno: závod se
   * počítá jako neúčast (0) všem – stejně jako dosud; takové závody vrací
   * `missingRaceIds`, ať obrazovka ukáže načítání místo hotové tabulky.
   */
  entriesByRace: { [raceId: string]: RaceResult[] | undefined };
  year: number;
  /**
   * Disciplína hodnocení. "all" míchá kulovnici s malorážkou do jednoho
   * průměru (původní chování) – obrazovka ho podle plánu nenabízí; výchozí "all"
   * jen kvůli shodě s původním kódem.
   */
  discipline?: DisciplineId | "all";
  mode: SeasonValueMode;
  bestN: BestRacesCount;
  /** Filtr kategorie (id z categories.ts); "all" / null / undefined = všechny. */
  category?: ResultCategoryId | "all" | null;
}

export interface SeasonRankingRow {
  sequenceNumber: string;
  /** Jméno z prvního výskytu střelce v roce (resultEntryName – nikdy e-mail ani uid). */
  name: string;
  /** Uložená (legacy) kategorie z prvního výskytu; bez hodnoty "Neznámá" – 1:1. */
  category: string;
  /** Zařazení prvního výskytu přes resolveEntryCategoryId (categoryId, jinak text); null = nezařazeno. */
  categoryId: ResultCategoryId | null;
  /** Hodnota (% nebo body) pro KAŽDÝ závod roku; neúčast = 0. */
  raceValues: { [raceId: string]: number };
  /** Průměr z nejlepších N (nuly se počítají, dělí se N); % na 2, body na 1 desetinné místo. */
  averageValue: number;
  /** Závody, které se do průměru nepočítají (škrtnuté). */
  ignoredRaceIds: Set<string>;
}

export interface SeasonRanking {
  /** Řádky seřazené podle průměru sestupně (při shodě pořadí prvního výskytu – 1:1). */
  rows: SeasonRankingRow[];
  /** Hodnocené závody roku a disciplíny vzestupně podle data = sloupce tabulky. */
  races: SeasonRace[];
  /** Počet hodnocených závodů (název 1:1 s původním kódem) – horní mez výběru N. */
  availableRaceCounts: number;
  /** Závody bez načtených výsledků (chybí v entriesByRace). */
  missingRaceIds: string[];
}

/**
 * Závody roku zahrnuté do ročního hodnocení (includeInResults a nevypnuté
 * výsledky), volitelně jedné disciplíny, vzestupně podle data – 1:1 s filtrem
 * v generateYearlySummary. Pro stav načítání volat s discipline "all".
 */
export function seasonRaces(
  races: SeasonRace[],
  year: number,
  discipline: DisciplineId | "all" = "all"
): SeasonRace[] {
  return races
    .filter((r) => r.year === year && r.includeInResults && r.enableResults !== false)
    .filter((r) => discipline === "all" || r.discipline === discipline)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

/**
 * Roční hodnocení „nejlepších N závodů" – přesun generateYearlySummary
 * z UserResults 1:1:
 *  1) střelci = kdo se v roce aspoň jednou objevil (jméno a kategorie
 *     z prvního výskytu),
 *  2) každému hodnota pro KAŽDÝ závod roku (neúčast / nenačteno = 0);
 *     v režimu % podíl z vítěze závodu (computeRacePercent),
 *  3) průměr = součet N nejlepších hodnot / N (nuly se počítají); při shodě
 *     hodnot se počítají dřívější závody, pozdější jsou škrtnuté,
 *  4) řazení podle průměru sestupně; shoda průměru zachová pořadí prvního
 *     výskytu (vč. pořadí klíčů objektu – číselná členská čísla bez
 *     počáteční nuly řadí JS napřed; záměrně ponecháno 1:1).
 * Filtr kategorie porovnává normalizované id (resolveEntryCategoryId – nejdřív
 * uložené categoryId, jinak legacy text), ne uložený text (oprava #8).
 */
export function computeSeasonRanking(input: SeasonRankingInput): SeasonRanking {
  const { entriesByRace, mode, bestN } = input;
  const discipline = input.discipline ?? "all";
  const categoryFilter =
    input.category === undefined || input.category === null ? "all" : input.category;

  const yearlyRaces = seasonRaces(input.races, input.year, discipline);

  const shooters: { [seq: string]: SeasonRankingRow } = {};

  // 1) seznam střelců, kteří se v roce aspoň jednou objevili
  yearlyRaces.forEach((race) => {
    const results = entriesByRace[race.id];
    results?.forEach((full) => {
      const seq = full.entry.sequenceNumber;

      if (!shooters[seq]) {
        const category = full.entry.category || "Neznámá";
        shooters[seq] = {
          name: resultEntryName(full.entry),
          category,
          categoryId: resolveEntryCategoryId(full.entry),
          sequenceNumber: seq,
          raceValues: {},
          averageValue: 0,
          ignoredRaceIds: new Set<string>(),
        };
      }
    });
  });

  // 2) pro každého střelce hodnota pro KAŽDÝ závod v roce (neúčast = 0)
  yearlyRaces.forEach((race) => {
    const results = entriesByRace[race.id];

    // topScore = body 1. místa v tomto závodě (přepočet % relativně k vítězi)
    const topScore = raceTopScore(results);

    Object.keys(shooters).forEach((seqNum) => {
      const participantResult = results?.find(
        (r) => r.entry.sequenceNumber === seqNum
      );

      shooters[seqNum].raceValues[race.id] = participantResult
        ? mode === "percent"
          ? computeRacePercent(participantResult.entry, { topScore })
          : participantResult.entry.matchPoints
        : 0;
    });
  });

  // 3) průměr: "Nejlepší N" = TOP N hodnot, ale dělí se N (nuly se počítají)
  const rows: SeasonRankingRow[] = Object.values(shooters)
    .filter((s) => categoryFilter === "all" || s.categoryId === categoryFilter)
    .map((shooter) => {
      const raceEntries = Object.entries(shooter.raceValues);

      const sortedEntries = [...raceEntries].sort(([, a], [, b]) => b - a);

      const totalRaces = sortedEntries.length;
      const n = bestN === "all" ? totalRaces : Math.min(bestN, totalRaces);

      const topEntries = n > 0 ? sortedEntries.slice(0, n) : [];

      const ignoredRaceIds = new Set<string>(
        n > 0 ? sortedEntries.slice(n).map(([raceId]) => raceId) : []
      );

      const sum = topEntries.reduce((acc, [, val]) => acc + val, 0);
      const avg = n > 0 ? sum / n : 0;

      return {
        ...shooter,
        averageValue: parseFloat(avg.toFixed(mode === "percent" ? 2 : 1)),
        ignoredRaceIds,
      };
    });

  rows.sort((a, b) => b.averageValue - a.averageValue);

  return {
    rows,
    races: yearlyRaces,
    availableRaceCounts: yearlyRaces.length,
    missingRaceIds: yearlyRaces
      .filter((r) => entriesByRace[r.id] === undefined)
      .map((r) => r.id),
  };
}

/**
 * Možnosti výběru „nejlepších N": 1–5 omezené počtem hodnocených závodů
 * (stejně jako dosavadní select), vždy navíc "all".
 */
export function bestRacesOptions(availableRaceCounts: number): BestRacesCount[] {
  const options: BestRacesCount[] = [];
  for (let n = 1; n <= 5; n++) {
    if (n <= availableRaceCounts) options.push(n);
  }
  options.push("all");
  return options;
}

/** Popisek volby N: "Nejlepší 1 závod" / "2–4 závody" / "5 závodů" / "Všechny závody". */
export function bestRacesLabel(n: BestRacesCount): string {
  if (n === "all") return "Všechny závody";
  return `Nejlepší ${n} ${n === 1 ? "závod" : n < 5 ? "závody" : "závodů"}`;
}
